import React from 'react';
import { Check, Clock, Target, Palette, Users, BarChart, Settings } from 'lucide-react';

interface BusinessArea {
  id: string;
  title: string;
  icon: React.ReactNode;
  tasks: {
    title: string;
    completed: boolean;
    description?: string;
  }[];
  isUnlocked: boolean;
}

export default function SetupChecklist() {
  const businessAreas: BusinessArea[] = [
    {
      id: 'vision',
      title: 'Vision & Strategy',
      icon: <Target className="w-5 h-5" />,
      isUnlocked: true,
      tasks: [
        { title: 'Define Mission Statement', completed: true },
        { title: 'Set Core Values', completed: true },
        { title: 'Identify Target Market', completed: false },
        { title: 'Create Business Plan', completed: false }
      ]
    },
    {
      id: 'brand',
      title: 'Brand Identity',
      icon: <Palette className="w-5 h-5" />,
      isUnlocked: true,
      tasks: [
        { title: 'Design Brand Guidelines', completed: true },
        { title: 'Create Logo', completed: true },
        { title: 'Define Brand Voice', completed: false },
        { title: 'Design Marketing Materials', completed: false }
      ]
    },
    {
      id: 'market',
      title: 'Market Presence',
      icon: <Users className="w-5 h-5" />,
      isUnlocked: false,
      tasks: [
        { title: 'Setup Social Media', completed: false },
        { title: 'Create Content Strategy', completed: false },
        { title: 'Build Email List', completed: false },
        { title: 'Launch Website', completed: false }
      ]
    },
    {
      id: 'operations',
      title: 'Operations',
      icon: <Settings className="w-5 h-5" />,
      isUnlocked: false,
      tasks: [
        { title: 'Setup Business Email', completed: false },
        { title: 'Choose Tools & Software', completed: false },
        { title: 'Create Work Processes', completed: false },
        { title: 'Setup Payment System', completed: false }
      ]
    }
  ];

  const calculateProgress = (area: BusinessArea) => {
    const completed = area.tasks.filter(t => t.completed).length;
    return Math.round((completed / area.tasks.length) * 100);
  };

  return (
    <div className="space-y-6">
      {businessAreas.map((area) => (
        <div 
          key={area.id}
          className={`rounded-lg border ${
            area.isUnlocked 
              ? 'border-blue-100 bg-white' 
              : 'border-gray-100 bg-gray-50'
          } p-4`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className={`${
                area.isUnlocked ? 'text-blue-500' : 'text-gray-400'
              }`}>
                {area.icon}
              </div>
              <h3 className={`font-medium ${
                area.isUnlocked ? 'text-gray-900' : 'text-gray-500'
              }`}>
                {area.title}
              </h3>
            </div>
            <span className="text-sm text-gray-500">
              {calculateProgress(area)}%
            </span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div 
              className={`h-2 rounded-full transition-all duration-500 ${
                area.isUnlocked ? 'bg-blue-500' : 'bg-gray-400'
              }`}
              style={{ width: `${calculateProgress(area)}%` }}
            />
          </div>

          {area.isUnlocked && (
            <ul className="space-y-2">
              {area.tasks.map((task, index) => (
                <li key={index} className="flex items-center space-x-3 text-sm">
                  <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full ${
                    task.completed 
                      ? 'bg-blue-500 text-white' 
                      : 'border-2 border-gray-300'
                  }`}>
                    {task.completed ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <Clock className="w-3 h-3 text-gray-400" />
                    )}
                  </span>
                  <span className={task.completed ? 'text-gray-500 line-through' : 'text-gray-700'}>
                    {task.title}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {!area.isUnlocked && (
            <div className="text-sm text-gray-500 flex items-center justify-center py-2">
              <Clock className="w-4 h-4 mr-2" />
              Complete previous steps to unlock
            </div>
          )}
        </div>
      ))}
    </div>
  );
}